import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { Login } from "@mui/icons-material";
import { AppBar, Box, Container, IconButton, Toolbar } from "@mui/material";
import { DE, ES, GB } from "country-flag-icons/react/3x2";
import { Link, useI18next } from "gatsby-plugin-react-i18next";
import { NavItem } from "../Common/NavItem";
import { scrollTo } from "../Helpers/helpers";
import Logo from "./Logo";
import MenuItem from "./MenuItem";
import SmallDevicesMenuIcon from "./SmallDevicesMenuIcon";

type TopMenuProps = {
  navItems: NavItem[];
  useScroll: boolean;
};

const isBrowser = typeof window !== `undefined`;

const TopMenu = ({ navItems, useScroll }: TopMenuProps) => {
  const { languages, originalPath, t, navigate } = useI18next();

  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const handleClick = () => {
    setIsOpenMenu((previous) => !previous);
  };

  const getWindowDimensions = (): Dimension | undefined => {
    if (!isBrowser) return;
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height,
    };
  };

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  const [pos] = useState(isBrowser ? window.document.documentElement.scrollTop : 0);
  const [imgLight, setImgLight] = useState(true);

  const handleOnClickMenu = (item: NavItem) => {
    if (isOpenMenu) setIsOpenMenu(false);

    switch (item.type) {
      case "home":
        if (useScroll) {
          if (!isBrowser) return;

          let element = document.getElementById(item.idnm);
          if (!element) return;
          scrollTo(window.pageYOffset || document.documentElement.scrollTop, element.offsetTop, 800);
        } else {
          navigate(`/#${item.idnm}`);
        }
        break;
      case "outside-home":
        navigate(item.href!);
        break;
      case "external":
        if (!isBrowser) return;
        window.location.href = item.href!;
        break;
      default:
        break;
    }
  };

  const getFlag = (language: string) => {
    switch (language) {
      case "en":
        return <GB title={t(`languages.${language}`)!} width={24} />;
      case "es":
        return <ES title={t(`languages.${language}`)!} width={24} />;
      case "de":
        return <DE title={t(`languages.${language}`)!} width={24} />;
      default:
        return <GB title={t("languages.en")!} width={24} />;
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (!isBrowser) return;

      let dimessions = getWindowDimensions();

      if (!dimessions) return;

      setWindowDimensions(dimessions);
      let scrollup = window.document.documentElement.scrollTop;

      if (dimessions.width < 1200 || scrollup > pos) {
        setImgLight(false);
      } else {
        setImgLight(true);
      }

      setIsOpenMenu(false);
    };

    const scrollNavigation = () => {
      if (!isBrowser) return;

      let scrollup = window.document.documentElement.scrollTop;

      if (windowDimensions && windowDimensions.width >= 1200) {
        if (scrollup > pos) {
          setImgLight(false);
        } else {
          setImgLight(true);
        }
      }
    };

    if (!isBrowser) return;

    let scrollup = window.document.documentElement.scrollTop;

    if ((windowDimensions && windowDimensions.width < 1200) || scrollup > pos) {
      setImgLight(false);
    } else {
      setImgLight(true);
    }

    window.addEventListener("scroll", scrollNavigation, true);
    window.addEventListener("resize", handleResize);

    return () => {
      if (!isBrowser) return;
      window.removeEventListener("scroll", scrollNavigation, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [pos, windowDimensions]);

  return (
    <>
      <AppBar
        sx={{
          backgroundColor: imgLight ? "rgba(4, 3, 3, 0.6)" : "white",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Logo imgLight={imgLight} onClick={() => navigate("/")} />

            <SmallDevicesMenuIcon onClick={handleClick} />

            <Box sx={{ display: { xs: "none", lg: "flex", marginLeft: "auto" } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {navItems.map((item, key) => (
                  <MenuItem onClick={handleOnClickMenu} key={key} item={item} imgLight={imgLight} />
                ))}
                {languages.map((lng) => (
                  <Box padding={1} key={lng}>
                    <Link
                      to={originalPath}
                      language={lng}
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      {getFlag(lng)}
                    </Link>
                  </Box>
                ))}
                <IconButton href="https://app.lanchaslagaviota.com">
                  <Login
                    sx={{
                      color: imgLight ? "white" : "#343a40",
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
          {isOpenMenu && (
            <Box>
              {navItems.map((item, key) => (
                <MenuItem onClick={handleOnClickMenu} key={key} item={item} imgLight={imgLight} />
              ))}
              {languages.map((lng) => (
                <Box padding={1} key={lng}>
                  <Link
                    to={originalPath}
                    language={lng}
                    placeholder={undefined}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    {getFlag(lng)}
                  </Link>
                </Box>
              ))}
              <IconButton href="https://app.lanchaslagaviota.com">
                <Login
                  sx={{
                    color: imgLight ? "white" : "#343a40",
                  }}
                />
              </IconButton>
            </Box>
          )}
        </Container>
      </AppBar>
    </>
  );
};

TopMenu.propTypes = {
  navItems: PropTypes.array.isRequired,
  useScroll: PropTypes.bool,
};

export default TopMenu;
